import {
  StatusEncoderOperationMode,
  State,
  StatusStreamState
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import { post, clear } from '#~/notifications/index.js';
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep.js';
import { applySettings, enableVideo } from './settings.js';
import type { Device } from '#~/device/index.js';
import { reboot } from '#~/device/commands/index.js';
import {
  DeviceRegistryCommandKey,
  DeviceRegistryProfileLevel
} from '@tryiris-ai/common.protojs/api/v1/device_settings';

export const OperationModeNotification = (
  mode: StatusEncoderOperationMode,
  deviceID: string,
  deviceName: string,
  requiresReboot: boolean,
  applySettings?: () => void,
  onEnableVideo?: () => void
) => {
  const messageId = `high-load-warning-${deviceID}`;
  let severity: 'warning' | 'error' = 'warning';
  let mainMessage: string;

  if (
    mode === StatusEncoderOperationMode.OPERATION_MODE_MINIMAL_SAFE ||
    mode === StatusEncoderOperationMode.OPERATION_MODE_RECOVERY
  ) {
    severity = 'error';
    mainMessage = `Critical resource usage detected on device ${deviceName}.`;
    if (onEnableVideo) {
      mainMessage += ' Video has been automatically paused to reduce load.';
    }
  } else if (mode === StatusEncoderOperationMode.OPERATION_MODE_DEGRADED) {
    severity = 'warning';
    mainMessage =
      `Device is using high system resources.` +
      (applySettings ? ` Switch to a safe profile to improve performance and reliability.` : '');
  } else {
    return;
  }

  const keepCurrentSettingsButton = (
    <span
      style={{
        color: '#6b7280',
        cursor: 'pointer',
        textDecoration: 'underline',
        opacity: 0.7
      }}
      onClick={() => clear(messageId)}
    >
      Keep Current Settings
    </span>
  );

  const enableVideoButton = (
    <span
      style={{
        color: '#2563eb',
        cursor: 'pointer',
        textDecoration: 'underline'
      }}
      onClick={onEnableVideo}
    >
      Re-enable video
    </span>
  );

  const applySettingsButton = (
    <span
      style={{
        color: '#2563eb',
        cursor: 'pointer',
        textDecoration: 'underline'
      }}
      onClick={applySettings}
    >
      {requiresReboot ? 'Apply profile and reboot' : 'Apply safe profile'}
    </span>
  );

  const enableVideoAndApplySettingsButton = (
    <span
      style={{
        color: '#2563eb',
        cursor: 'pointer',
        textDecoration: 'underline'
      }}
      onClick={() => {
        if (applySettings) {
          applySettings();
        }
        if (onEnableVideo) {
          onEnableVideo();
        }
      }}
    >
      {requiresReboot ? 'Apply profile and reboot' : 'Apply safe profile'}
    </span>
  );

  post(
    {
      actionable: Boolean(applySettings || onEnableVideo),
      id: messageId,
      message: (
        <span>
          {mainMessage}
          {applySettings && !onEnableVideo && (
            <>
              <br />
              <span style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
                {keepCurrentSettingsButton}
                {applySettingsButton}
              </span>
            </>
          )}
          {applySettings && onEnableVideo && (
            <>
              <br />
              <span style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
                {enableVideoButton}
                {enableVideoAndApplySettingsButton}
              </span>
            </>
          )}
          {!applySettings && onEnableVideo && (
            <>
              <br />
              <span style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
                {enableVideoButton}
              </span>
            </>
          )}
        </span>
      ),
      severity: severity,
      title: `${deviceName} Performance Warning`,
      timestamp: new Date()
    },
    { autoClose: onEnableVideo ? false : 15_000 }
  );
};

export const ProfileAppliedNotification = (
  profile: DeviceRegistryProfileLevel,
  deviceID: string,
  deviceName: string,
  reboot?: () => void
) => {
  const messageId = `profile-applied-${profile}-${deviceID}`;
  let mainMessage: string;
  switch (profile) {
    case DeviceRegistryProfileLevel.PROFILE_LEVEL_MID:
      mainMessage = `${deviceName} - The device video settings were changed to ensure proper performance on device.`;
      break;
    default:
      mainMessage = `${deviceName} - The device video settings have been optimized for performance on device.`;
  }

  const rebootButton = reboot ? (
    <span
      style={{
        color: '#2563eb',
        cursor: 'pointer',
        textDecoration: 'underline'
      }}
      onClick={reboot}
    >
      {'Reboot now'}
    </span>
  ) : null;

  post(
    {
      actionable: Boolean(reboot),
      id: messageId,
      message: (
        <span>
          {mainMessage}
          {rebootButton && (
            <>
              <br />
              <span style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
                {rebootButton}
              </span>
            </>
          )}
        </span>
      ),
      severity: 'warning',
      title: `${deviceName} Video settings changed`,
      timestamp: new Date()
    },
    { autoClose: rebootButton ? false : 10_000 }
  );
};

export const notifyOperationMode = (
  mode: StatusEncoderOperationMode,
  state: ReadonlyObjectDeep<State>
) => {
  const studioDevices = state.control?.studio?.device;
  const devices = state.status?.device;

  const [device, deviceId] = (() => {
    for (const [deviceId, device] of Object.entries(devices || {})) {
      if (device?.capabilities?.video) {
        return [device, deviceId];
      }
    }
    return [undefined, undefined];
  })();

  if (!device) {
    return;
  }

  const onEnableVideo =
    device?.video?.status?.state == StatusStreamState.STREAM_STATE_PAUSED
      ? () => {
          enableVideo(deviceId as Device['id']);
          clear(`high-load-warning-${deviceId}`);
        }
      : undefined;

  const onApplySettings =
    device?.deviceSettings?.suggestedSettings &&
    Object.keys(device.deviceSettings.suggestedSettings).length > 0
      ? () => {
          clear(`high-load-warning-${deviceId}`);
          applySettings(deviceId as Device['id']);
        }
      : undefined;

  const requiresReboot = Object.values(device?.deviceSettings?.suggestedSettings || {}).some(
    (setting) =>
      setting.key === DeviceRegistryCommandKey.POWER ||
      setting.key === DeviceRegistryCommandKey.SYSTEM_REBOOT
  );

  OperationModeNotification(
    mode,
    deviceId,
    studioDevices?.[deviceId]?.name || `Device ${deviceId}`,
    requiresReboot,
    onApplySettings,
    onEnableVideo
  );
};

export const notifyProfileApplied = (
  profile: DeviceRegistryProfileLevel,
  deviceId: string,
  requiresReboot: boolean,
  state: ReadonlyObjectDeep<State>
) => {
  const studioDevices = state.control?.studio?.device;
  const device = studioDevices?.[deviceId];

  if (!device) {
    return;
  }

  const onReboot = requiresReboot
    ? () => {
        reboot(deviceId as Device['id']);
      }
    : undefined;

  ProfileAppliedNotification(profile, deviceId, device.name || `Device ${deviceId}`, onReboot);
};
